import React, { useState, useEffect, useContext } from 'react';
import { Card, CardContent, Typography, Grid, Box, Button, Divider, Modal, Backdrop, Fade, TextField } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { API } from '../controller/api';
import Cookies from 'js-cookie';
import { DrawerContext } from '../controller/context/DrawerWidthContext';
import { TitleContext } from '../controller/context/TitleContext';
import { useStripe, useElements, CardElement } from '@stripe/react-stripe-js';
import { CreditCard, DateRange, InfoOutlined } from '@mui/icons-material'; // Import icons

import axios from 'axios';

const AccountPage = () => {
  const [userData, setUserData] = useState(null);
  const [loadingPayment, setLoadingPayment] = useState(false);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [paymentError, setPaymentError] = useState(null);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [passwordError, setPasswordError] = useState(null);

  const stripe = useStripe();
  const elements = useElements();
const [update, setUpdate] = useState(false)
  const navigate = useNavigate();
  const { drawerWidth } = useContext(DrawerContext);
  const { setTitle } = useContext(TitleContext);

  useEffect(() => {
    setTitle('Account Settings');
    const fetchUserData = async () => {
      try {
        const token = Cookies.get('token');
        const response = await axios.post(`${API}/auth/refresh-user`, { token });
        setUserData(response.data.user);

        // Fetch payment information after loading user data
        const paymentResponse = await axios.post(`${API}/subscription/payment-info`, { email: response.data.user.email });
        if (paymentResponse.data.success) {
          setUserData((prevUserData) => ({
            ...prevUserData,
            paymentId: paymentResponse.data.paymentMethod,
          }));
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      }
    };
    fetchUserData();
  }, [update]);

  if (!userData) {
    return <div>Loading...</div>;
  }

  // Function to handle subscription cancellation
  const handleCancelSubscription = async () => {
    // Show confirmation dialog with additional details
    const confirmed = window.confirm(
      'Are you sure you want to cancel your subscription? You will retain access until the end of your current subscription period.'
    );
  
    if (!confirmed) {
      return; // Exit the function if the user does not confirm
    }
  
    try {
      const response = await axios.post(`${API}/subscription/cancel`, {
        email: userData.email,
      });
  
      if (response.data.success) {
        // Update the userData state to reflect subscription cancellation
        setUserData((prevUserData) => ({
          ...prevUserData,
          subscription: { ...prevUserData.subscription, isCanceled: true },
        }));
        alert('Your subscription has been successfully canceled. You will continue to have access until your subscription period ends.');
      } else {
        setPaymentError('Error canceling subscription.');
      }
    } catch (error) {
      console.error('Error canceling subscription:', error);
      setPaymentError('Error processing subscription cancellation.');
    }
  };
  

  // Function to handle subscription renewal
  const handleRenewSubscription = async () => {
    // Check if there is a payment method associated with the user
    if (!userData.paymentId) {
      // Ask the user to add a payment method first
      const addPaymentMethod = window.confirm(
        'To renew your subscription, please add a payment method. Would you like to add one now?'
      );
  
      if (addPaymentMethod) {
        // Show the payment form for the user to add a payment method
        setShowPaymentForm(true);
      }
      return; // Exit the function since payment method is required before renewal
    }
  
    // If a payment method exists, proceed with renewal
    try {
      const response = await axios.post(`${API}/subscription/renew`, {
        email: userData.email,
      });
  
      if (response.data.success) {
        // Update the userData state to reflect subscription renewal
        setUserData((prevUserData) => ({
          ...prevUserData,
          subscription: { ...prevUserData.subscription, isCanceled: false },
        }));
        alert('Your subscription has been successfully renewed.');
      } else {
        setPaymentError('Error renewing subscription.');
      }
    } catch (error) {
      console.error('Error renewing subscription:', error);
      setPaymentError('Error processing subscription renewal.');
    }
  };

  const handleAddPaymentMethod = async () => {
    setShowPaymentForm(true);  // Show Stripe Elements form
  };

  const handlePaymentSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      // Stripe.js hasn't loaded yet
      return;
    }

    setLoadingPayment(true);

    const cardElement = elements.getElement(CardElement);

    // Create a PaymentMethod using Stripe Elements
    const { error, paymentMethod } = await stripe.createPaymentMethod({
      type: 'card',
      card: cardElement,
    });
    if (error) {
      setPaymentError(error.message);
      setLoadingPayment(false);
      return;
    }

    try {
      // Send the paymentMethod.id to your server to create a payment method and associate it with the customer
      const response = await axios.post(`${API}/subscription/save-payment-method`, {
        userId: userData.email,
        paymentMethodId: paymentMethod.id,
        name: `${userData.firstName} ${userData.lastName}`
      });

      if (response.data.success) {
        // Update userData or show success message
        setUserData({ ...userData, paymentInformation: response.data.paymentInformation });
        setShowPaymentForm(false);
        setUpdate(!update)

      } else {
        setPaymentError('Error saving payment method.');
      }
    } catch (error) {
      setPaymentError('Error processing payment.');
    }

    setLoadingPayment(false);
  };
  

  // Custom styles for the CardElement
  const cardElementStyles = {
    style: {
      base: {
        fontSize: '16px',
        color: '#424770',
        letterSpacing: '0.025em',
        fontFamily: '"Roboto", sans-serif',
        '::placeholder': {
          color: '#aab7c4',
        },
      },
      invalid: {
        color: '#9e2146',
      },
    },
  };

  const handleRemovePaymentMethod = async () => {
    try {
      const response = await axios.post(`${API}/subscription/remove-payment-method`, {
        email: userData.email,
      });

      if (response.data.success) {
        // Update the userData state to reflect that there is no payment method
        setUserData((prevUserData) => ({
          ...prevUserData,
          paymentInformation: { ...prevUserData.paymentInformation, customerId: 'NA' },
          paymentId: null,
        }));
      } else {
        setPaymentError('Error removing payment method.');
      }
    } catch (error) {
      console.error('Error removing payment method:', error);
      setPaymentError('Error processing payment removal.');
    }
  };

  const handleOpenChangePasswordModal = () => setShowChangePasswordModal(true);
  const handleCloseChangePasswordModal = () => setShowChangePasswordModal(false);

  const handleChangePassword = async () => {
    // Validate passwords
    if (newPassword !== confirmPassword) {
      setPasswordError("New passwords don't match.");
      return;
    }

    try {
      const response = await axios.post(`${API}/auth/change-password`, {
        email: userData.email,
        currentPassword,
        newPassword,
      });

      if (response.data.success) {
        alert('Password successfully changed.');
        handleCloseChangePasswordModal();
      } else {
        setPasswordError(response.data.message || 'Error changing password.');
      }
    } catch (error) {
      console.error('Error changing password:', error);
      setPasswordError('Error processing password change.');
    }
  };

  return (
    <div style={{ overflowY: 'auto', maxHeight: '100vh', paddingBottom: 100, marginTop: 40 }}>
      <Box
        sx={{
          marginBottom: '10px',
          marginLeft: { xs: drawerWidth === 0 ? 38 : 0, sm: drawerWidth === 0 ? 38 : 0 },
          padding: 5,
        }}
      >
        <Grid container spacing={3}>
          {/* User Info Section */}
      <Grid item xs={12} md={12}>
        <Typography variant="h6" gutterBottom>
          User Information
        </Typography>
        <Card>
          <CardContent>
            <Typography variant="body1">
              <strong>Name:</strong> {userData.firstName} {userData.lastName}
            </Typography>
            <Typography variant="body1">
              <strong>Email:</strong> {userData.email}
            </Typography>
            <Button variant="outlined" color="primary" onClick={handleOpenChangePasswordModal} sx={{ marginTop: 2 }}>
              Change Password
            </Button>
          </CardContent>
        </Card>
      </Grid>

      {/* Change Password Modal */}
      <Modal
        open={showChangePasswordModal}
        onClose={handleCloseChangePasswordModal}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{ timeout: 500 }}
      >
        <Fade in={showChangePasswordModal}>
          <Box sx={{ width: 400, backgroundColor: 'white', borderRadius: 2, padding: 3, margin: 'auto', top: '20%', position: 'relative' }}>
            <Typography variant="h6" gutterBottom>
              Change Password
            </Typography>
            <TextField
              label="Current Password"
              type="password"
              fullWidth
              margin="normal"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
            />
            <TextField
              label="New Password"
              type="password"
              fullWidth
              margin="normal"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />
            <TextField
              label="Confirm New Password"
              type="password"
              fullWidth
              margin="normal"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
            {passwordError && <Typography color="error">{passwordError}</Typography>}
            <Button variant="contained" color="primary" onClick={handleChangePassword} sx={{ marginTop: 2 }}>
              Submit
            </Button>
          </Box>
        </Fade>
      </Modal>

          {/* Subscription Info Section */}
          <Grid item xs={12} md={12}>
            <Typography variant="h6" gutterBottom>
              Subscription Information
            </Typography>
            <Card>
              <CardContent>
                <Typography variant="body1">
                  <strong>Status:</strong> {userData.subscription.isActive ? 'Active' : 'Inactive'}
                </Typography>
                <Typography variant="body1">
                  <strong>{userData.subscription.isCanceled ? 'End' : 'Renewal'} Date:</strong>{' '}
                  {new Date(userData.subscription.renewalDate).toLocaleDateString()}
                </Typography>
                <Typography variant="body1">
                  <strong>Start Date:</strong> {new Date(userData.subscription.startDate).toLocaleDateString()}
                </Typography>
                <Typography variant="body1">
                  <strong>Training Access:</strong> {userData.subscription.training ? 'Lifetime' : 'No Access'}
                </Typography>
                <Typography variant="body1">
                  <strong>Subscription Auto-Renew:</strong>{' '}
                  {userData.subscription.isCanceled ? 'Disabled' : 'Enabled'}
                </Typography>
              </CardContent>
              <Divider />
              <CardContent>
                {userData.subscription.isCanceled ? (
                  <Button variant="contained" color="primary" onClick={handleRenewSubscription} sx={{ marginRight: 2 }}>
                    Renew Subscription
                  </Button>
                ) : (
                  <Button variant="outlined" color="error" onClick={handleCancelSubscription}>
                    Cancel Subscription
                  </Button>
                )}
              </CardContent>
            </Card>
          </Grid>

           {/* Payment Info Section */}
           <Grid item xs={12} md={12}>
            <Typography variant="h6" gutterBottom>
              Payment Information
            </Typography>
            <Card>
              <CardContent>
                {userData.paymentInformation.customerId !== 'NA' ? (
                  <>
                    <Grid container alignItems="center" spacing={2}>
                      <Grid item>
                        <CreditCard fontSize="large" color="primary" />
                      </Grid>
                      <Grid item xs={9}>
                        <Typography variant="body1">
                          <strong>Brand:</strong> {userData?.paymentId?.brand.toUpperCase()}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <Typography variant="body1" display="flex" alignItems="center">
                          <InfoOutlined fontSize="small" sx={{ mr: 1 }} />
                          <strong>Last 4:</strong> {userData?.paymentId?.last4}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <Typography variant="body1" display="flex" alignItems="center">
                          <DateRange fontSize="small" sx={{ mr: 1 }} />
                          <strong>Expiry:</strong> {`${userData?.paymentId?.exp_month}/${userData?.paymentId?.exp_year}`}
                        </Typography>
                      </Grid>
                    </Grid>

                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleAddPaymentMethod}
                      sx={{ marginTop: 3 }}
                    >
                      Update Payment Method
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={handleRemovePaymentMethod}
                      sx={{ marginTop: 2, marginLeft: 2 }}
                    >
                      Remove Payment Method
                    </Button>
                  </>
                ) : (
                  <>
                    <Typography variant="body1">No payment method added.</Typography>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleAddPaymentMethod}
                      sx={{ marginTop: 2 }}
                      disabled={loadingPayment}
                    >
                      {loadingPayment ? 'Adding...' : 'Add Payment Method'}
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* Stripe Payment Form Modal */}
      <Modal
        open={showPaymentForm}
        onClose={() => setShowPaymentForm(false)}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
      >
        <Fade in={showPaymentForm}>
          <Box
            sx={{
              width: 400,
              backgroundColor: 'white',
              borderRadius: 2,
              padding: 3,
              margin: 'auto',
              top: '20%',
              position: 'relative',
            }}
          >
            <Typography variant="h6" gutterBottom>
              Add Payment Method
            </Typography>
            <form onSubmit={handlePaymentSubmit}>
              <CardElement options={cardElementStyles} />
              {paymentError && <div style={{ color: 'red' }}>{paymentError}</div>}
              <Button
                variant="contained"
                color="primary"
                type="submit"
                disabled={!stripe || !elements || loadingPayment}
                sx={{ marginTop: 2 }}
              >
                {loadingPayment ? 'Processing...' : 'Submit Payment Method'}
              </Button>
            </form>
          </Box>
        </Fade>
      </Modal>
    </div>
  );
};

export default AccountPage;
